import { RHFAutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/RHFAutoCompleteVirtualize.tsx';
import { type DealFormData, dealFormField } from '@openx/types/deal.ts';
import { produceSortedOptionsKeys } from '@openx/utils/lib/sortedOptions.ts';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type Path, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDealContext } from '../../../utils/context/DealContext.tsx';
import type { IUseBrandsFetch } from '../../../utils/context/DealHookTypes.ts';
import { RebateInfo } from '../../DealRevenueCalculator/DealRevenueModifiersContext/components/RebateInfo.tsx';
import { useHasDealRevenueCalculatorAccess } from '../../DealRevenueCalculator/hooks/useHasDealRevenueCalculatorAccess.ts';
import { DealParticipantsField } from '../fields.ts';

type ParticipantBrandsProps = {
  index: number;
};

export const ParticipantBrands = ({ index }: ParticipantBrandsProps) => {
  const { useBrandsFetch } = useDealContext();

  if (!useBrandsFetch) {
    return null;
  }

  return <ParticipantBrandsRender index={index} useBrandsFetch={useBrandsFetch} />;
};

type ParticipantBrandsRenderProps = {
  useBrandsFetch: IUseBrandsFetch;
} & ParticipantBrandsProps;

export const ParticipantBrandsRender = ({ index, useBrandsFetch }: ParticipantBrandsRenderProps) => {
  const brandIdsFieldName: Path<DealFormData> =
    `${dealFormField.DEAL_PARTICIPANTS}.${index}.${DealParticipantsField.BRAND_IDS}` as const;

  const { setValue } = useFormContext();
  const hasDealRevenueCalculatorAccess = useHasDealRevenueCalculatorAccess();
  const focused = useRef(false);
  const [searchedBrand, setSearchedBrand] = useState('');
  const { t } = useTranslation();

  const observableBrands = useWatch<DealFormData, typeof brandIdsFieldName>({ name: brandIdsFieldName });

  const { mapById: brands = {}, loading: isBrandsLoading, refetch: refetchBrands } = useBrandsFetch(observableBrands);

  useEffect(() => {
    if (searchedBrand.length >= 2) {
      refetchBrands(searchedBrand);
    }
  }, [refetchBrands, searchedBrand]);

  const onBrandSearch = useCallback(e => {
    setSearchedBrand(e.target.value);
  }, []);

  const brandOptions = useMemo(() => produceSortedOptionsKeys(brands), [brands]);

  // reset removed brands
  useEffect(() => {
    if (observableBrands.some(brand => brand === null)) {
      return;
    }
    let markDeletedBrands: (string | null)[] = observableBrands;
    if (observableBrands.length && Object.keys(brands).length && !isBrandsLoading) {
      markDeletedBrands = observableBrands.map(brand => (brands[brand] ? brand : null));
    }
    if (!isEqual(markDeletedBrands, observableBrands)) {
      setValue(brandIdsFieldName, markDeletedBrands, {
        shouldTouch: true,
        shouldValidate: true,
      });
    }
  }, [brandIdsFieldName, observableBrands, brands, isBrandsLoading, setValue]);

  return (
    <>
      <RHFAutoCompleteVirtualize
        name={brandIdsFieldName}
        textFieldProps={{
          label: t('Brands (Optional)'),
          margin: 'dense',
          onBlur: () => {
            focused.current = false;
          },
          onFocus: () => {
            focused.current = true;
          },
          placeholder: t('Enter Brand Names'),
        }}
        getOptionLabel={option => brands[option]?.name || t('N / A')}
        options={brandOptions}
        renderOptions={{
          tooltip: true,
        }}
        multiple
        loading={isBrandsLoading && focused.current}
        data-test="deal-brand-ids"
        onDebounceChange={onBrandSearch}
        debounced
        limitTags={99}
      />

      {hasDealRevenueCalculatorAccess && <RebateInfo index={index} />}
    </>
  );
};
